import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { COLORS } from "../../theme/colors";
import { MATERIALS, RADIUS } from "../../theme/materials";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { supervisorApi, approvalApi, voiceQaApi, agentApi } from "../../api/client";
import { Badge, Button, Input, IconButton } from "../../components/ui";
import PageHeader from "../../components/PageHeader";
import RecentOutboundCallsCard from "./RecentOutboundCallsCard";
import ConversationsFilterBar from "../analytics/ConversationsFilterBar";
import { exportConversationsCsv } from "./exportConversationsCsv";

const PRIORITY_COLORS = { urgent: COLORS.pink, high: COLORS.orange, normal: COLORS.accent, low: COLORS.textMuted };

// Human-readable conversation duration. Exported for unit testing.
export const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return "--";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
};

// Channel → emoji, defaulting to the whatsapp bubble. Exported for unit testing.
export const channelIcon = (ch) => ({ whatsapp: "💬", chat: "🌐", voice: "📞", sms: "📱", email: "✉" }[ch] || "💬");

const SupervisorView = ({ currentUser }) => {
  const bp = useBreakpoint();
  const navigate = useNavigate();
  const [dashData, setDashData] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [supTab, setSupTab] = useState("live");
  const [pendingActions, setPendingActions] = useState([]);
  const [approvalStats, setApprovalStats] = useState(null);
  const [voiceCalls, setVoiceCalls] = useState([]);
  const [voiceQaFilter, setVoiceQaFilter] = useState("all");
  const [selectedTranscript, setSelectedTranscript] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [convoTotal, setConvoTotal] = useState(0);
  const [convoLoading, setConvoLoading] = useState(false);
  const [convoError, setConvoError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [convoOffset, setConvoOffset] = useState(0);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [exporting, setExporting] = useState(false);
  // Phase 1.5 #3 · extended trace search filters (Conversations tab only).
  // Filter values flow into supervisorApi.conversations(...). Reset offset
  // when filters change so pagination starts fresh.
  const [filterQ, setFilterQ] = useState("");
  const [filterChannels, setFilterChannels] = useState([]);  // multi-select
  const [filterAgentId, setFilterAgentId] = useState("");
  const [filterEscalated, setFilterEscalated] = useState(null);  // null = any
  const [agentOptions, setAgentOptions] = useState([]);
  // Debounce the text input so we don't fire a request per keystroke
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filterQ.trim()), 300);
    return () => clearTimeout(t);
  }, [filterQ]);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resolveLoading, setResolveLoading] = useState(null);
  const [resolveSummary, setResolveSummary] = useState("");
  const [agentPerf, setAgentPerf] = useState([]);
  const [perfLoading, setPerfLoading] = useState(false);
  const [perfDays, setPerfDays] = useState(7);
  // null = all-time (preserves pre-BUG-007 Supervisor behavior). 7/30/90 windows the
  // escalated + handed_off counts server-side so the user can reconcile against
  // Analytics' windowed figures. Active + Resolved Today are always real-time and
  // don't change with this setting.
  const [dashDays, setDashDays] = useState(null);

  // Dashboard KPIs — poll every 15s, reload on window change
  useEffect(() => {
    const load = async () => {
      try {
        const res = await supervisorApi.dashboard(dashDays);
        if (res.success) setDashData(res.data);
      } catch {}
      setDashLoading(false);
    };
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [dashDays]);

  // Conversations list — poll every 15s.
  // - "live"   = in-progress (status started/active) — poll, drawer open
  // - "queue"  = escalation review (status escalated) — poll, drawer open
  // - "conversations" = FINISHED (status resolved/abandoned/completed) —
  //   poll less frequently is fine but we keep the same 15s for uniformity.
  //   Row click navigates DIRECTLY to /analytics/conversations/{id} (no drawer)
  //   because finished convos are debugged in the full-page Inspector.
  useEffect(() => {
    if (supTab === "performance" || supTab === "approvals" || supTab === "voiceqa") return;
    const load = async () => {
      setConvoLoading(true);
      try {
        let statuses;
        if (supTab === "live") statuses = ["started", "active"];
        else if (supTab === "queue") statuses = ["escalated"];
        else if (supTab === "conversations") statuses = ["resolved", "ended", "abandoned", "completed"];
        else statuses = ["started", "active"];
        // Phase 1.5 #3 · only the Conversations tab uses extended filters;
        // Live + Queue keep their simple shape so the UX doesn't change
        // for the realtime monitoring flows.
        const useFilters = supTab === "conversations";
        const res = await supervisorApi.conversations(
          statuses,
          useFilters ? (filterAgentId || null) : null,
          useFilters && filterChannels.length ? filterChannels : null,
          20,
          convoOffset,
          dashDays,
          useFilters ? (debouncedQ || null) : null,
          useFilters ? filterEscalated : null,
        );
        if (res.success) {
          setConversations(res.data.conversations);
          setConvoTotal(res.data.total);
          setConvoError(null);
        } else {
          // A {success:false} response is a real failure — surface it instead
          // of falling through to the "no conversations, widen your date range"
          // empty state, which is indistinguishable from an actual empty window.
          setConvoError(res.error || "Could not load conversations.");
        }
      } catch (e) {
        // Previously a bare `catch {}` swallowed every error (expired JWT, 401,
        // network) and left `conversations` empty, so the UI showed "widen your
        // date range" on a turn the API had actually failed — costing real
        // debugging time before anyone checked the network tab. Surface it.
        setConvoError(e?.message || "Could not load conversations.");
      }
      setConvoLoading(false);
    };
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [supTab, convoOffset, dashDays, debouncedQ, filterChannels, filterAgentId, filterEscalated, reloadTick]);

  // Reset offset when filters change so pagination starts fresh
  useEffect(() => {
    if (convoOffset !== 0) setConvoOffset(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, filterChannels, filterAgentId, filterEscalated]);

  // Load agent list once for the agent filter dropdown
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await agentApi.list();
        if (!active) return;
        const list = Array.isArray(res) ? res : (res?.data || []);
        setAgentOptions(Array.isArray(list) ? list : []);
      } catch { /* non-fatal */ }
    })();
    return () => { active = false; };
  }, []);

  // Agent performance
  useEffect(() => {
    if (supTab !== "performance") return;
    const load = async () => {
      setPerfLoading(true);
      try {
        const res = await supervisorApi.agentPerformance(perfDays);
        if (res.success) setAgentPerf(res.data);
      } catch {}
      setPerfLoading(false);
    };
    load();
  }, [supTab, perfDays]);

  // Conversation detail
  useEffect(() => {
    if (!selectedConvo) { setDetailData(null); return; }
    const load = async () => {
      setDetailLoading(true);
      try {
        const res = await supervisorApi.conversationDetail(selectedConvo);
        if (res.success) setDetailData(res.data);
      } catch {}
      setDetailLoading(false);
    };
    load();
  }, [selectedConvo]);

  const handleResolve = async (conversationId) => {
    setResolveLoading(conversationId);
    try {
      const res = await supervisorApi.resolveConversation(conversationId, resolveSummary || null);
      if (res.success) {
        setConversations(prev => prev.filter(c => c.id !== conversationId));
        setResolveSummary("");
        setSelectedConvo(null);
        const dashRes = await supervisorApi.dashboard(dashDays);
        if (dashRes.success) setDashData(dashRes.data);
      }
    } catch {}
    setResolveLoading(null);
  };

  const statusBadge = (status) => {
    const colors = {
      started: { bg: `${COLORS.textDim}20`, text: COLORS.textMuted },
      active: { bg: `${COLORS.green}15`, text: COLORS.green },
      escalated: { bg: `${COLORS.pink}15`, text: COLORS.pink },
      resolved: { bg: `${COLORS.accent}15`, text: COLORS.accent },
      // "ended" = normal completion (agent did reply, no explicit close
      // signal, idle > timeout); distinct from "abandoned" (true drop-off
      // before any AI engagement). See conversation_repo.abandon_stale.
      ended: { bg: `${COLORS.textMuted}15`, text: COLORS.textMuted },
      completed: { bg: `${COLORS.green}15`, text: COLORS.green },
      abandoned: { bg: `${COLORS.orange}15`, text: COLORS.orange },
    };
    const c = colors[status] || colors.started;
    return <Badge color={c.text} style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>{status}</Badge>;
  };

  // Windowed tiles (Escalated) show an explicit scope badge so clients can
  // reconcile against Analytics' rolling window — BUG-007.
  const windowLabel = dashDays ? `Last ${dashDays} days` : "All time";
  const kpiCards = dashData ? [
    { label: "Active Conversations", value: dashData.active_count, color: COLORS.green, tab: "live" },
    { label: "Escalated (Pending)", value: dashData.escalated_count, color: COLORS.pink, tab: "queue", subLabel: windowLabel },
    { label: "Resolved Today", value: dashData.resolved_today, color: COLORS.accentText },
    { label: "Avg CSAT", value: dashData.avg_csat ? `${dashData.avg_csat} / 5` : "--", color: COLORS.orange },
  ] : [];

  // Load voice calls for QA
  useEffect(() => {
    if (supTab === "voiceqa") {
      voiceQaApi.listCalls(voiceQaFilter === "all" ? null : voiceQaFilter).then(r => { if (r.success) setVoiceCalls(r.data || []); }).catch(() => {});
    }
  }, [supTab, voiceQaFilter]);

  // Load pending approvals
  useEffect(() => {
    if (supTab === "approvals") {
      approvalApi.list("pending").then(r => { if (r.success) setPendingActions(r.data || []); }).catch(() => {});
      approvalApi.stats().then(r => { if (r.success) setApprovalStats(r.data); }).catch(() => {});
    }
  }, [supTab]);

  const supTabs = [
    { id: "live", label: "Live Conversations" },
    { id: "queue", label: "Review Queue" },
    // Finished conversations — entry point for the Conversation Inspector.
    // Live + Review Queue cover active/escalation flows; this tab is the
    // historical/debug surface that closes the Phase 1 discoverability gap.
    { id: "conversations", label: "Conversations" },
    { id: "approvals", label: "Approvals", badge: pendingActions.length || null },
    { id: "voiceqa", label: "Voice QA" },
    { id: "performance", label: "Agent Performance" },
  ];

  const totalPages = Math.ceil(convoTotal / 20);
  const currentPage = Math.floor(convoOffset / 20) + 1;

  return (
    <div className="panel-scroll" style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 32 }}>
      <PageHeader
        title="Supervisor"
        subtitle="Monitor conversations, review escalations, track agent performance"
        filters={
          <div style={{ display: "flex", gap: 6 }}>
            {[
              { label: "All", days: null },
              { label: "7D", days: 7 },
              { label: "30D", days: 30 },
              { label: "90D", days: 90 },
            ].map(p => {
              const active = dashDays === p.days;
              return (
                <Button key={p.label} size="sm" variant={active ? "primary" : "secondary"}
                  onClick={() => setDashDays(p.days)}
                  style={active
                    ? { background: COLORS.accentSoft, color: COLORS.accentText, border: `1px solid ${COLORS.accent}`, padding: "6px 14px", fontSize: 12 }
                    : { padding: "6px 14px", fontSize: 12 }
                  }>
                  {p.label}
                </Button>
              );
            })}
          </div>
        }
      />

      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
        {kpiCards.map((kpi, i) => (
          <div key={i} onClick={kpi.tab ? () => { setSupTab(kpi.tab); setConvoOffset(0); } : undefined} style={{
            background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12,
            padding: "16px 18px", display: "flex", flexDirection: "column", gap: 6,
            cursor: kpi.tab ? "pointer" : "default", transition: "border-color 0.2s",
          }}
          onMouseEnter={e => { if (kpi.tab) e.currentTarget.style.borderColor = kpi.color; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>{kpi.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: kpi.color }}>{dashLoading ? "..." : kpi.value}</div>
            {kpi.subLabel && (
              <div style={{ fontSize: 10, color: COLORS.textDim, letterSpacing: "0.02em", marginTop: -2 }}>{kpi.subLabel}</div>
            )}
          </div>
        ))}
      </div>

      {/* Tab Bar */}
      <div style={{ display: "flex", gap: 0, marginBottom: 20, borderBottom: `1px solid ${COLORS.border}` }}>
        {supTabs.map(tab => (
          <button key={tab.id} onClick={() => { setSupTab(tab.id); setConvoOffset(0); }} style={{
            padding: "10px 20px", border: "none", cursor: "pointer", background: "transparent",
            fontSize: 12, fontWeight: 600, color: supTab === tab.id ? COLORS.text : COLORS.textMuted,
            borderBottom: supTab === tab.id ? `2px solid ${COLORS.accent}` : "2px solid transparent",
            transition: "all 0.2s",
          }}>{tab.label}{tab.id === "queue" && dashData?.escalated_count > 0 && (
            <Badge color={COLORS.pink} style={{ marginLeft: 6, fontSize: 10, padding: "2px 6px", borderRadius: 8 }}>
              {dashData.escalated_count}
            </Badge>
          )}</button>
        ))}
      </div>

      {/* Live Conversations / Review Queue / Conversations (finished) */}
      {(supTab === "live" || supTab === "queue" || supTab === "conversations") && (
        <div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, marginBottom: 8 }}>
            <button disabled={exporting} onClick={async () => {
              setExportError(null);
              setExporting(true);
              await exportConversationsCsv({
                days: 30,
                onError: (err) => setExportError(err?.message || "Export failed"),
              });
              setExporting(false);
            }} style={{
              padding: "6px 14px", fontSize: 11, borderRadius: 6,
              border: `1px solid ${COLORS.border}`, background: COLORS.surface,
              color: COLORS.text, cursor: exporting ? "wait" : "pointer",
              opacity: exporting ? 0.6 : 1,
            }}>{"⬇"} {exporting ? "Exporting…" : "Export CSV"}</button>
            {exportError && (
              <span style={{ fontSize: 11, color: COLORS.pink }}>{exportError}</span>
            )}
          </div>
          {/* Phase 1.5 #3 · filter bar — Conversations tab only.
              Live + Queue keep the simple shape so realtime monitoring
              doesn't get an unfamiliar surface. */}
          {supTab === "conversations" && (
            <div style={{ marginBottom: 12 }}>
              <ConversationsFilterBar
                q={filterQ}
                onQChange={setFilterQ}
                channels={filterChannels}
                onChannelsChange={setFilterChannels}
                agentId={filterAgentId}
                onAgentIdChange={setFilterAgentId}
                escalated={filterEscalated}
                onEscalatedChange={setFilterEscalated}
                agentOptions={agentOptions}
              />
            </div>
          )}
          {convoLoading && conversations.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: COLORS.textMuted, fontSize: 13 }}>Loading conversations...</div>
          ) : convoError && conversations.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: COLORS.red || "#ef4444", fontSize: 13 }}>
              <div style={{ marginBottom: 10 }}>Couldn't load conversations — {convoError}</div>
              <button
                onClick={() => { setConvoError(null); setReloadTick(t => t + 1); }}
                style={{ padding: "6px 14px", fontSize: 12, borderRadius: 8, cursor: "pointer",
                  background: "transparent", color: COLORS.textMuted, border: `1px solid ${COLORS.border}` }}
              >Retry</button>
            </div>
          ) : conversations.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: COLORS.textMuted, fontSize: 13 }}>
              {supTab === "live"
                ? "No active conversations"
                : supTab === "queue"
                ? "No escalated conversations pending review"
                : "No finished conversations in this window — try widening the date range"}
            </div>
          ) : (
            <>
              {/* Table header */}
              <div style={{
                display: "grid", gridTemplateColumns: bp.isMobile ? "1fr 1fr 60px" : "1.5fr 1.2fr 80px 70px 90px 80px 40px",
                gap: 8, padding: "8px 14px", fontSize: 10, fontWeight: 700, color: COLORS.textDim,
                textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: `1px solid ${COLORS.border}`,
              }}>
                <div>Contact</div><div>Agent</div>{!bp.isMobile && <><div>Channel</div><div>Msgs</div><div>Duration</div></>}<div>Status</div><div></div>
              </div>

              {/* Table rows.
                  Live + Review Queue: row click opens transcript drawer
                  (the operator monitors / takes action).
                  Conversations (finished): row click navigates DIRECTLY
                  to the full-page Inspector — debugging finished convos
                  doesn't need the drawer's resolve-action affordances. */}
              {conversations.map(c => (
                <div
                  key={c.id}
                  onClick={() => {
                    if (supTab === "conversations") {
                      navigate(`/analytics/conversations/${c.id}`);
                    } else {
                      setSelectedConvo(c.id);
                    }
                  }}
                  style={{
                  display: "grid", gridTemplateColumns: bp.isMobile ? "1fr 1fr 60px" : "1.5fr 1.2fr 80px 70px 90px 80px 40px",
                  gap: 8, padding: "12px 14px", alignItems: "center", cursor: "pointer",
                  borderBottom: `1px solid ${COLORS.border}08`, transition: "background 0.15s",
                  background: selectedConvo === c.id ? COLORS.accentSoft : "transparent",
                }} onMouseEnter={e => e.currentTarget.style.background = COLORS.surfaceHover}
                   onMouseLeave={e => e.currentTarget.style.background = selectedConvo === c.id ? COLORS.accentSoft : "transparent"}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {/*
                      contact_display is the Identity-Nexus-aware label
                      computed by the backend (supervisor.py
                      _format_contact_display). Falls back through:
                      contact_name → profile.name → profile.phone → profile.email
                      → channel-aware smart-format of user_id (full phones,
                      full emails, "Web visitor" for session UUIDs).
                      The legacy `c.user_id?.slice(0, 8)` produced
                      truncated phones / emails / random UUID prefixes —
                      see the bug fix at supervisor.py 2026-05-10.
                    */}
                    {c.contact_display || c.contact_name || c.user_id || "Unknown"}
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.agent_name || "--"}</div>
                  {!bp.isMobile && (
                    <>
                      <div style={{ fontSize: 12, color: COLORS.textMuted }}>{channelIcon(c.channel)} {c.channel}</div>
                      <div style={{ fontSize: 12, color: COLORS.textMuted, textAlign: "center" }}>{c.message_count || 0}</div>
                      <div style={{ fontSize: 12, color: COLORS.textMuted }}>{formatDuration(c.duration_seconds)}</div>
                    </>
                  )}
                  <div>{statusBadge(c.status)}</div>
                  {/* Last column: direct-to-Inspector affordance.
                      For ALL tabs, the icon button bypasses the drawer
                      and navigates straight to the full inspector — the
                      drawer is for monitoring/actions, the inspector is
                      for debugging (spans/replay/trace). The button is
                      visible on Live + Queue + Conversations so the
                      user has a consistent drill-down path everywhere
                      (user-reported 2026-06-24 — they couldn't find
                      how to inspect a live chat). Click on the row
                      ITSELF still opens the drawer on Live/Queue, but
                      this button takes the explicit shortcut. */}
                  <button
                    type="button"
                    title="Open in Inspector (debug spans / replay)"
                    aria-label="Open in Inspector"
                    data-testid="row-inspector-link"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/analytics/conversations/${c.id}`);
                    }}
                    style={{
                      background: "transparent",
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: 4,
                      color: COLORS.accent,
                      cursor: "pointer",
                      fontSize: 11, fontWeight: 600,
                      padding: "4px 8px",
                      fontFamily: "inherit",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Inspect ↗
                  </button>
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, padding: "16px 0" }}>
                  <Button variant="secondary" size="sm" disabled={currentPage <= 1}
                    onClick={() => setConvoOffset(Math.max(0, convoOffset - 20))}
                    style={{ padding: "6px 14px", background: COLORS.surface }}>
                    Prev
                  </Button>
                  <span style={{ fontSize: 12, color: COLORS.textMuted }}>{currentPage} / {totalPages}</span>
                  <Button variant="secondary" size="sm" disabled={currentPage >= totalPages}
                    onClick={() => setConvoOffset(convoOffset + 20)}
                    style={{ padding: "6px 14px", background: COLORS.surface }}>
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Agent Performance */}
      {supTab === "performance" && (
        <div>
          {/* Period selector */}
          <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
            {[7, 30, 90].map(d => (
              <Button key={d} variant={perfDays === d ? "primary" : "secondary"} size="sm"
                onClick={() => setPerfDays(d)}
                style={perfDays === d
                  ? { background: COLORS.accentSoft, color: COLORS.accentText, border: `1px solid ${COLORS.accent}`, padding: "6px 14px", fontSize: 12 }
                  : { background: COLORS.surface, padding: "6px 14px", fontSize: 12 }
                }>
                {d}D
              </Button>
            ))}
          </div>

          {perfLoading ? (
            <div style={{ textAlign: "center", padding: 40, color: COLORS.textMuted, fontSize: 13 }}>Loading agent performance...</div>
          ) : agentPerf.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: COLORS.textMuted, fontSize: 13 }}>No agent data for this period</div>
          ) : (
            <>
              {/* Table header */}
              <div style={{
                display: "grid", gridTemplateColumns: bp.isMobile ? "1fr 60px 60px" : "1.5fr 70px 90px 100px 80px 80px",
                gap: 8, padding: "8px 14px", fontSize: 10, fontWeight: 700, color: COLORS.textDim,
                textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: `1px solid ${COLORS.border}`,
              }}>
                <div>Agent</div>{!bp.isMobile && <div>Status</div>}<div>Convos</div>
                {!bp.isMobile && <div>Resolution</div>}<div>Escalated</div>{!bp.isMobile && <div>Avg CSAT</div>}
              </div>

              {agentPerf.map(a => {
                const rateColor = a.resolution_rate >= 80 ? COLORS.green : a.resolution_rate >= 50 ? COLORS.orange : COLORS.pink;
                return (
                  <div key={a.agent_id} style={{
                    display: "grid", gridTemplateColumns: bp.isMobile ? "1fr 60px 60px" : "1.5fr 70px 90px 100px 80px 80px",
                    gap: 8, padding: "12px 14px", alignItems: "center", borderBottom: `1px solid ${COLORS.border}08`,
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{a.agent_name || a.agent_id?.slice(0, 8)}</div>
                    {!bp.isMobile && (
                      <div><Badge color={a.agent_status === "live" ? COLORS.green : COLORS.textMuted}>{a.agent_status}</Badge></div>
                    )}
                    <div style={{ fontSize: 13, color: COLORS.text, fontWeight: 600 }}>{a.conversations}</div>
                    {!bp.isMobile && (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ flex: 1, height: 4, borderRadius: 2, background: `${COLORS.border}` }}>
                          <div style={{ height: "100%", borderRadius: 2, background: rateColor, width: `${Math.min(a.resolution_rate, 100)}%`, transition: "width 0.3s" }} />
                        </div>
                        <span style={{ fontSize: 11, color: rateColor, fontWeight: 600, minWidth: 36 }}>{a.resolution_rate}%</span>
                      </div>
                    )}
                    <div style={{ fontSize: 13, color: a.escalated > 0 ? COLORS.pink : COLORS.textMuted, textAlign: bp.isMobile ? "center" : "left" }}>{a.escalated}</div>
                    {!bp.isMobile && (
                      <div style={{ fontSize: 13, color: a.avg_csat ? COLORS.orange : COLORS.textDim }}>{a.avg_csat ? `${a.avg_csat} / 5` : "--"}</div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* Conversation Detail Slide-out */}
      {selectedConvo && (
        <>
          {/* Backdrop */}
          <div onClick={() => setSelectedConvo(null)} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 199,
          }} />

          {/* Panel */}
          <div style={{
            position: "fixed", top: 0, right: 0, width: bp.isMobile ? "100%" : 480,
            height: "100vh", background: COLORS.surface, borderLeft: `1px solid ${COLORS.border}`,
            zIndex: 200, display: "flex", flexDirection: "column",
            boxShadow: "-8px 0 32px rgba(0,0,0,0.4)",
          }}>
            {/* Header */}
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
                  {detailData?.conversation?.contact_display
                    || detailData?.conversation?.contact_name
                    || detailData?.conversation?.user_id
                    || "Conversation"}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, color: COLORS.textMuted }}>{detailData?.conversation?.agent_name}</span>
                  <span style={{ fontSize: 11, color: COLORS.textDim }}>|</span>
                  <span style={{ fontSize: 11, color: COLORS.textMuted }}>{channelIcon(detailData?.conversation?.channel)} {detailData?.conversation?.channel}</span>
                  <span style={{ fontSize: 11, color: COLORS.textDim }}>|</span>
                  {detailData?.conversation && statusBadge(detailData.conversation.status)}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {/* Deep-link into the Conversation Inspector for the full
                    span/trace/replay view. Opens via React Router (same SPA),
                    not a new tab — operator stays in the platform. */}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => navigate(`/analytics/conversations/${selectedConvo}`)}
                  title="Open in Conversation Inspector"
                  style={{ padding: "6px 12px", fontSize: 12, whiteSpace: "nowrap" }}
                >
                  Open in Inspector ↗
                </Button>
                <IconButton icon="×" onClick={() => setSelectedConvo(null)}
                  style={{ background: COLORS.surface }} />
              </div>
            </div>

            {/* Tags */}
            <ConversationTags conversationId={selectedConvo} />

            {/* D10 — Cross-link to recent outbound voice activity. Hides
                itself unless the resolved customer profile has calls
                in the last 72h. Lets the operator stitch context for
                "they're calling us because we left them a VM" cases. */}
            <RecentOutboundCallsCard
              profileId={detailData?.conversation?.customer_profile_id} />

            {/* Messages */}
            <div className="panel-scroll" style={{ flex: 1, overflowY: "auto", padding: 20 }}>
              {detailLoading ? (
                <div style={{ textAlign: "center", padding: 40, color: COLORS.textMuted, fontSize: 13 }}>Loading messages...</div>
              ) : !detailData?.messages?.length ? (
                <div style={{ textAlign: "center", padding: 40, color: COLORS.textMuted, fontSize: 13 }}>No messages</div>
              ) : (
                detailData.messages.map(msg => (
                  <div key={msg.id} style={{
                    display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                    marginBottom: 12,
                  }}>
                    <div style={{
                      maxWidth: "80%", padding: "10px 14px", borderRadius: 12,
                      background: msg.role === "user" ? COLORS.accent : COLORS.bg,
                      color: msg.role === "user" ? "#fff" : COLORS.text,
                      border: msg.role !== "user" ? `1px solid ${COLORS.border}` : "none",
                    }}>
                      <div style={{ fontSize: 9, fontWeight: 700, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em",
                        color: msg.role === "user" ? "rgba(255,255,255,0.6)" : COLORS.textDim }}>
                        {msg.role === "user" ? "Customer" : "Agent"}
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{msg.content}</div>
                      <div style={{ fontSize: 10, marginTop: 4, color: msg.role === "user" ? "rgba(255,255,255,0.4)" : COLORS.textDim }}>
                        {msg.created_at ? new Date(msg.created_at).toLocaleTimeString() : ""}
                        {msg.latency_ms ? ` · ${msg.latency_ms}ms` : ""}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Resolve action bar (only for escalated) */}
            {detailData?.conversation?.status === "escalated" && (
              <div style={{
                padding: "12px 20px", borderTop: `1px solid ${COLORS.border}`,
                display: "flex", gap: 8, alignItems: "center",
              }}>
                <Input placeholder="Resolution summary (optional)" value={resolveSummary}
                  onChange={e => setResolveSummary(e.target.value)}
                  style={{ flex: 1, padding: "8px 12px", borderRadius: 8 }} />
                <Button onClick={() => handleResolve(selectedConvo)}
                  disabled={resolveLoading === selectedConvo}
                  loading={resolveLoading === selectedConvo}
                  style={{ background: COLORS.green, border: "none" }}>
                  Resolve
                </Button>
              </div>
            )}
          </div>
        </>
      )}
      {/* Voice QA */}
      {supTab === "voiceqa" && (
        <div>
          <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
            {["all", "unreviewed", "reviewed"].map(f => (
              <Button key={f} size="sm" variant={voiceQaFilter === f ? "primary" : "secondary"}
                onClick={() => setVoiceQaFilter(f)}
                style={voiceQaFilter === f ? { background: COLORS.accentSoft, color: COLORS.accentText, border: `1px solid ${COLORS.accent}` } : {}}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </Button>
            ))}
          </div>

          {voiceCalls.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: COLORS.textDim }}>
              <div style={{ fontSize: 30, marginBottom: 8 }}>{"\uD83C\uDFA4"}</div>
              <div style={{ fontSize: 13, color: COLORS.textMuted }}>No voice calls to review</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {voiceCalls.map((c, idx) => (
                // i2-visual-identity-v2 §10.3 C1 — Voice call row card.
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    type: "spring", stiffness: 400, damping: 25,
                    delay: Math.min(idx * 0.04, 0.32),
                  }}
                  whileHover={{ y: -2, transition: { type: "spring", stiffness: 400, damping: 25 } }}
                  style={{
                    padding: 14,
                    borderRadius: RADIUS.inner,
                    background: COLORS.surface,
                    border: `1px solid ${c.qa_score ? COLORS.green + "25" : COLORS.border}`,
                    boxShadow: MATERIALS.spatialShadow(),
                  }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 16 }}>{"\uD83D\uDCDE"}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{c.contact_display || c.contact_name || c.user_id}</div>
                        <div style={{ fontSize: 10, color: COLORS.textDim }}>
                          {c.duration_seconds ? `${Math.floor(c.duration_seconds / 60)}m ${c.duration_seconds % 60}s` : "—"}
                          {" \u00B7 "}{c.intent || "general"}
                          {" \u00B7 "}{c.started_at ? new Date(c.started_at).toLocaleString() : ""}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      {c.qa_score && (
                        <div style={{ display: "flex", gap: 2 }}>
                          {[1,2,3,4,5].map(s => (
                            <span key={s} style={{ fontSize: 12, color: s <= c.qa_score ? COLORS.orange : COLORS.textDim }}>{"\u2605"}</span>
                          ))}
                        </div>
                      )}
                      <Badge color={c.status === "resolved" ? COLORS.green : c.status === "escalated" ? COLORS.pink : COLORS.textMuted}>
                        {c.status}
                      </Badge>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    {c.has_transcript && (
                      <Button size="sm" onClick={async () => {
                        const r = await voiceQaApi.getTranscript(c.id);
                        if (r.success) setSelectedTranscript({ call: c, data: r.data });
                      }} style={{ background: `${COLORS.cyan}15`, color: COLORS.cyan, border: `1px solid ${COLORS.cyan}30` }}>
                        View Transcript
                      </Button>
                    )}
                    {c.audio_path && (
                      <Button size="sm" onClick={async () => {
                        // Fetch the audio as an authed blob (through
                        // `/api/voice/calls/{id}/audio-stream`) and
                        // open a local `blob:` URL. We can't use a
                        // MinIO presigned URL directly because its
                        // signature is bound to the cluster-internal
                        // hostname which the browser can't resolve.
                        try {
                          const blobUrl = await voiceQaApi.fetchAudioBlobUrl(c.id);
                          window.open(blobUrl, "_blank");
                        } catch (err) {
                          alert(`Audio playback failed: ${err.message || err}`);
                        }
                      }} style={{ background: `${COLORS.accent}15`, color: COLORS.accentText, border: `1px solid ${COLORS.accent}30` }}>
                        Play Audio
                      </Button>
                    )}
                    {!c.qa_score && (
                      <Button size="sm" onClick={async () => {
                        const score = prompt("QA Score (1-5):");
                        if (!score || isNaN(score)) return;
                        const notes = prompt("QA Notes (optional):");
                        await voiceQaApi.submitQa(c.id, parseInt(score), notes);
                        voiceQaApi.listCalls(voiceQaFilter === "all" ? null : voiceQaFilter).then(r => { if (r.success) setVoiceCalls(r.data || []); });
                      }} style={{ background: `${COLORS.orange}15`, color: COLORS.orange, border: `1px solid ${COLORS.orange}30` }}>
                        Rate Call
                      </Button>
                    )}
                  </div>

                  {c.qa_notes && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 6 }}>QA: {c.qa_notes}</div>}
                </motion.div>
              ))}
            </div>
          )}

          {/* Transcript viewer */}
          {selectedTranscript && (
            <div style={{
              position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
              background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
            }} onClick={() => setSelectedTranscript(null)}>
              <div onClick={e => e.stopPropagation()} style={{
                width: 600, maxWidth: "90vw", maxHeight: "80vh", overflowY: "auto",
                background: COLORS.surface, borderRadius: 16, padding: 24,
                border: `1px solid ${COLORS.border}`, boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>Call Transcript</div>
                  <button onClick={() => setSelectedTranscript(null)} style={{ background: "none", border: "none", color: COLORS.textMuted, fontSize: 18, cursor: "pointer" }}>{"\u2715"}</button>
                </div>
                {/* The API (`GET /api/voice/calls/{id}/transcript` →
                    get_transcript) returns
                      { conversation_id, turns, turn_count, transcript, duration_seconds }
                    — NOT the raw ES document. Prefer `turns` (array of
                    {speaker, text, timestamp_ms}) for speaker-labeled
                    rendering, fall back to the flat `transcript` text,
                    and only then "No transcript content". Previously
                    read `.content` / `._source.content` which never
                    existed → every modal showed "No transcript content"
                    even though save_transcript had persisted turns
                    correctly in metadata_json.voice_transcript. Seen
                    2026-04-20 on staging. */}
                {Array.isArray(selectedTranscript.data?.turns) && selectedTranscript.data.turns.length > 0 ? (
                  <div style={{
                    fontSize: 12, color: COLORS.text, lineHeight: 1.6,
                    background: COLORS.surface, padding: 16, borderRadius: 10, border: `1px solid ${COLORS.border}`,
                    maxHeight: "60vh", overflowY: "auto",
                  }}>
                    {selectedTranscript.data.turns.map((t, i) => (
                      <div key={i} style={{ marginBottom: 8 }}>
                        <span style={{ fontWeight: 700, color: t.speaker === "agent" ? COLORS.accent : COLORS.cyan, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.05em" }}>
                          {t.speaker || "unknown"}
                        </span>
                        <span style={{ marginLeft: 8, whiteSpace: "pre-wrap" }}>{t.text}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <pre style={{
                    fontSize: 12, color: COLORS.text, lineHeight: 1.6, whiteSpace: "pre-wrap",
                    background: COLORS.surface, padding: 16, borderRadius: 10, border: `1px solid ${COLORS.border}`,
                  }}>{selectedTranscript.data?.transcript || selectedTranscript.data?.content || "No transcript content"}</pre>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Approvals */}
      {supTab === "approvals" && (
        <div>
          {/* Stats */}
          {approvalStats && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 16 }}>
              {[
                { label: "Pending", value: approvalStats.pending, color: COLORS.orange },
                { label: "Approved", value: approvalStats.approved, color: COLORS.green },
                { label: "Rejected", value: approvalStats.rejected, color: COLORS.pink },
                { label: "Expired", value: approvalStats.expired, color: COLORS.textMuted },
                { label: "Executed", value: approvalStats.executed, color: COLORS.accentText },
              ].map(s => (
                <div key={s.label} style={{ padding: 10, borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}`, textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: COLORS.textDim }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Pending actions */}
          {pendingActions.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: COLORS.textDim }}>
              <div style={{ fontSize: 30, marginBottom: 8 }}>{"\u2705"}</div>
              <div style={{ fontSize: 13, color: COLORS.textMuted }}>No pending approvals</div>
              <div style={{ fontSize: 11, marginTop: 4 }}>Actions requiring approval will appear here</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {pendingActions.map((a, idx) => (
                // i2-visual-identity-v2 §10.3 C1 — Pending action row card.
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    type: "spring", stiffness: 400, damping: 25,
                    delay: Math.min(idx * 0.04, 0.32),
                  }}
                  whileHover={{ y: -2, transition: { type: "spring", stiffness: 400, damping: 25 } }}
                  style={{
                    padding: 16,
                    borderRadius: RADIUS.inner,
                    background: COLORS.surface,
                    border: `1px solid ${(PRIORITY_COLORS[a.priority] || COLORS.border)}30`,
                    boxShadow: MATERIALS.spatialShadow(),
                  }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Badge color={PRIORITY_COLORS[a.priority] || COLORS.accent}>{a.priority}</Badge>
                      <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{a.action_type}</span>
                      {a.amount != null && <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.green }}>${a.amount.toFixed(2)}</span>}
                    </div>
                    <span style={{ fontSize: 10, color: COLORS.textDim }}>
                      {a.created_at ? new Date(a.created_at).toLocaleString() : ""}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.text, marginBottom: 6 }}>{a.description}</div>
                  {a.agent_reasoning && <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 6 }}>Agent reasoning: {a.agent_reasoning}</div>}
                  {a.customer_message && <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 8, fontStyle: "italic" }}>Customer: "{a.customer_message}"</div>}
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button size="sm" onClick={async () => {
                      await approvalApi.approve(a.id);
                      const r = await approvalApi.list("pending"); if (r.success) setPendingActions(r.data || []);
                      approvalApi.stats().then(r => { if (r.success) setApprovalStats(r.data); });
                    }} style={{ background: COLORS.green, border: "none", color: "#fff" }}>
                      Approve
                    </Button>
                    <Button size="sm" onClick={async () => {
                      const note = prompt("Rejection reason (optional):");
                      await approvalApi.reject(a.id, note);
                      const r = await approvalApi.list("pending"); if (r.success) setPendingActions(r.data || []);
                      approvalApi.stats().then(r => { if (r.success) setApprovalStats(r.data); });
                    }} style={{ background: `${COLORS.pink}15`, color: COLORS.pink, border: `1px solid ${COLORS.pink}30` }}>
                      Reject
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Conversation Tags Component ──────────────────────── */
function ConversationTags({ conversationId }) {
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [adding, setAdding] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (!conversationId) return;
    supervisorApi.getTags(conversationId).then(r => {
      if (r.success) setTags(r.data);
    }).catch(() => {});
    supervisorApi.tagSuggestions("").then(r => {
      if (r.success) setSuggestions(r.data);
    }).catch(() => {});
  }, [conversationId]);

  const handleAdd = async () => {
    const tag = newTag.trim().toLowerCase();
    if (!tag || adding) return;
    setAdding(true);
    try {
      const r = await supervisorApi.addTag(conversationId, tag);
      if (r.success) setTags(prev => [...prev, r.data]);
      setNewTag("");
    } catch (e) { /* duplicate */ }
    setAdding(false);
  };

  const handleRemove = async (tag) => {
    try {
      await supervisorApi.removeTag(conversationId, tag);
      setTags(prev => prev.filter(t => t.tag !== tag));
    } catch {}
  };

  const quickTags = ["vip", "follow-up", "training-example", "bug-report"];

  const sourceColor = (src) => src === "manual" ? COLORS.accent : src === "auto_agent" ? COLORS.cyan : COLORS.textDim;

  return (
    <div style={{ padding: "8px 16px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      {tags.map(t => (
        <span key={t.tag} style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "2px 8px", borderRadius: 12, fontSize: 10, fontWeight: 600,
          background: `${sourceColor(t.source)}22`, color: sourceColor(t.source),
          border: `1px solid ${sourceColor(t.source)}44`,
        }}>
          {t.tag}
          {t.source === "manual" && (
            <span onClick={() => handleRemove(t.tag)} style={{ cursor: "pointer", opacity: 0.6, marginLeft: 2 }}>×</span>
          )}
        </span>
      ))}
      <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <input
          value={newTag}
          onChange={e => setNewTag(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleAdd()}
          placeholder="+ tag"
          list="tag-suggestions"
          style={{
            width: 80, padding: "2px 6px", fontSize: 10, borderRadius: 8,
            border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.text,
            outline: "none",
          }}
        />
        <datalist id="tag-suggestions">
          {suggestions.filter(s => !tags.some(t => t.tag === s)).map(s => (
            <option key={s} value={s} />
          ))}
          {quickTags.filter(q => !tags.some(t => t.tag === q)).map(q => (
            <option key={q} value={q} />
          ))}
        </datalist>
      </div>
    </div>
  );
}

export default SupervisorView;
